
import { VueCropper } from 'vue-cropper'
import axios from 'axios'
import apiConfig from '@/config/api.config'
import { IMAGE_PURPOSES } from '@/config/imagePurposes'
import { computeMaxCropRect } from '@/utils/image'

export default {
  name: 'ImageUploader',
  components: {
    VueCropper
  },
  props: {
    // 默认比例 1:1, 可以传 "16:9", "free" 等
    ratio: {
      type: String,
      default: '1:1'
    },
    placeholder: {
      type: String,
      default: '点击上传图片'
    },
    // 是否支持上传到服务器，如果为false则只返回WebP Blob
    autoUpload: {
      type: Boolean,
      default: true
    },
    // 如果已有图片URL，可以传入作为初始预览
    initialImage: {
      type: String,
      default: ''
    },
    // 是否在上传成功后立即清空组件状态（适用于多图上传的”添加”按钮）
    clearAfterEmit: {
      type: Boolean,
      default: false
    },
    // 上传用途（product/banner/delivery/avatar/order/other）
    // 默认 'other' — 业务页面必须显式指定，避免误归类
    purpose: {
      type: String,
      default: 'other'
    }
  },
  data () {
    return {
      isCropping: false,
      previewImage: '',
      uploading: false,
      storageType: 'cos', // 默认使用 COS 存储
      showImagePreview: false, // 显示图片预览
      cropSizing: false, // 用户正在拖角/边缩放（此时才显示裁切 handles，平时弱化）
      option: {
        img: '', // 裁剪图片的地址
        size: 1, // 裁剪生成图片的质量
        outputType: 'webp', // 裁剪生成图片的格式 (Webp极大节省带宽)
        full: false, // 是否输出原图比例的截图
        canMove: true, // 上传图片是否可以移动（pan-first：拖图调构图是主操作）
        original: false, // 上传图片按照原始比例渲染
        canMoveBox: true, // 截图框能否拖动
        fixedBox: false, // 固定截图框大小 不允许改变（false=保留缩小/放大能力）
        fixed: true, // 是否开启截图框宽高固定比例
        fixedNumber: [1, 1], // 截图框的宽高比例
        centerBox: true, // 截图框是否被限制在图片里面（保证 crop box 内永不露背景）
        infoTrue: true // true 为展示真实输出图片宽高
      }
    }
  },
  computed: {
    // 生效比例（单一来源原则）：purpose 命中 imagePurposes 配置（如 home_banner/hub_cover）
    // 时由配置驱动预览框+裁剪锁+提示，管理页不再传 ratio prop（传了也以配置为准）；
    // 未命中配置的 purpose（product/banner 等）维持 ratio prop 行为不变
    effectiveRatio () {
      const conf = IMAGE_PURPOSES[this.purpose]
      return (conf && conf.ratioLabel) || this.ratio
    },
    purposeHint () {
      const conf = IMAGE_PURPOSES[this.purpose]
      return conf ? conf.hint : ''
    },
    uploaderStyle () {
      if (this.effectiveRatio === 'free') return { width: '120px', height: '120px' }
      const parts = this.effectiveRatio.split(':')
      if (parts.length === 2) {
        // parseFloat：小数比例（如 3.4:1）parseInt 会截成 3:1
        const w = parseFloat(parts[0]) || 1
        const h = parseFloat(parts[1]) || 1
        return {
          width: '100%',
          maxWidth: '300px',
          aspectRatio: `${w} / ${h}`
        }
      }
      return { width: '120px', height: '120px' }
    },
    parsedFixedNumber () {
      if (this.effectiveRatio === 'free') {
        return [1, 1] // ratio 'free' 会配合 fixed: false 使用
      }
      const parts = this.effectiveRatio.split(':')
      if (parts.length === 2) {
        return [parseFloat(parts[0]), parseFloat(parts[1])] // 小数比例支持（3.4:1）
      }
      return [1, 1]
    }
  },
  watch: {
    initialImage: {
      handler (val) {
        if (val && !this.previewImage) {
          this.previewImage = val
        }
      },
      immediate: true
    },
    ratio: {
      handler () { this.syncFixed() },
      immediate: true
    },
    purpose: {
      handler () { this.syncFixed() },
      immediate: true
    }
  },
  methods: {
    // 裁剪比例锁开关随生效比例（purpose 配置或 ratio prop）变化同步
    syncFixed () {
      this.option.fixed = this.effectiveRatio !== 'free'
    },
    triggerInput () {
      this.$refs.fileInput.click()
    },
    openImagePreview () {
      if (this.previewImage) {
        // 使用 Vant ImagePreview 打开图片预览
        this.$imagePreview({
          images: [this.$img(this.previewImage, 'original')],
          startPosition: 0,
          closeable: true
        })
      }
    },
    onFileSelected (e) {
      const file = e.target.files[0]
      if (!file) {
        return
      }
      // 验证类型
      if (!/\.(jpg|jpeg|png|webp|gif|heic)$/i.test(file.name)) {
        this.$q.notify({ message: '图片类型必须是jpeg,jpg,png,webp中的一种', color: 'negative', position: 'top' })
        return
      }

      // 将文件转换为blob url传给对应的cropper
      const reader = new FileReader()
      reader.onload = (e) => {
        let data
        if (typeof e.target.result === 'object') {
          // 把Array Buffer转化为blob 如果是base64不需要
          data = window.URL.createObjectURL(new Blob([e.target.result]))
        } else {
          data = e.target.result
        }
        this.option.img = data
        this.isCropping = true
      }
      // 转化为base64
      // reader.readAsDataURL(file)
      // 转化为blob
      reader.readAsArrayBuffer(file)
      // 重置input，允许重复选同一张图
      this.$refs.fileInput.value = ''
    },
    // 图片就绪（imgLoad success 时 trueWidth/trueHeight/scale/容器 w/h 均已就绪）→
    // 初始框最大化。w/h 尚为 0（弹窗动画中测量未落定）时延迟重试，最多 3 次
    onImgLoad (result) {
      if (result !== 'success') return
      this.maximizeCrop(0)
    },
    // 按目标比例最大化裁切框（rotate-aware：90° 奇数倍旋转时真实宽高交换，与
    // vue-cropper goAutoCrop 内部同口径）。free 场景目标比例=图片自身比例（整图最大）
    maximizeCrop (attempt) {
      const c = this.$refs.cropper
      if (!c) return
      if (!c.w || !c.h || !c.trueWidth) {
        if (attempt < 3) setTimeout(() => this.maximizeCrop(attempt + 1), 250)
        return
      }
      const swap = Math.abs(c.rotate) % 2 > 0
      const imgW = (swap ? c.trueHeight : c.trueWidth) * c.scale
      const imgH = (swap ? c.trueWidth : c.trueHeight) * c.scale
      const aspect = this.option.fixed
        ? this.parsedFixedNumber[0] / this.parsedFixedNumber[1]
        : imgW / imgH
      const rect = computeMaxCropRect(imgW, imgH, aspect)
      if (rect) c.goAutoCrop(rect.width, rect.height)
    },
    // 只在用户抓到缩放点/边线（主动进入调整裁切范围）时亮出 handles；
    // 拖动面（移动图片/框）不触发。抬起后归位弱化态
    onCropPointerDown (e) {
      const t = e.target
      if (t && t.closest && t.closest('.crop-point, .crop-line')) {
        this.cropSizing = true
        const up = () => {
          this.cropSizing = false
          window.removeEventListener('mouseup', up)
          window.removeEventListener('touchend', up)
        }
        window.addEventListener('mouseup', up)
        window.addEventListener('touchend', up)
      }
    },
    rotateRight () {
      this.$refs.cropper.rotateRight()
      // 旋转 90° 后图片宽高互换，旧框不再匹配新方向 → 重新按目标比例最大化
      this.$nextTick(() => this.maximizeCrop(0))
    },
    cancelCrop () {
      this.isCropping = false
      if (!this.previewImage) {
        this.option.img = ''
      }
    },
    clearImage () {
      this.previewImage = ''
      this.option.img = ''
      this.$emit('change', '')
    },
    // 上传图片（点击保存按钮）
    finishCrop () {
      this.uploading = true
      // 获取截图的blob数据
      this.$refs.cropper.getCropBlob(async (data) => {
        if (!this.autoUpload) {
          // 不自动上传，返回 WebP blob 给父组件自行处理
          const previewUrl = window.URL.createObjectURL(data)
          this.previewImage = previewUrl
          this.isCropping = false
          this.uploading = false
          this.$emit('crop-success', data, previewUrl)
          return
        }

        // 自动上传到后端
        const formData = new FormData()
        // 将 Blob 伪装成 file，这里强制设置后缀为 webp
        formData.append('file', data, `upload_${Date.now()}.webp`)
        // 添加存储位置参数
        formData.append('storage', this.storageType)
        // 生命周期标记（purpose 用于权限校验 + 孤儿清理）
        formData.append('purpose', this.purpose)

        try {
          // 直接使用相对路径，axios会自动加上baseURL
          const uploadUrl = '/upload'

          const res = await axios({
            url: uploadUrl,
            method: 'post',
            data: formData
          })

          if (res.data.code === 200 || (res.data && res.data.url)) {
            const url = res.data.data?.url || res.data.url
            if (this.clearAfterEmit) {
              this.previewImage = ''
              this.option.img = ''
            } else {
              this.previewImage = url
            }
            this.isCropping = false
            this.$q.notify({ message: '上传成功(' + (this.storageType === 'cos' ? '服务器' : '本地') + ')', color: 'positive', position: 'top' })
            this.$emit('change', url) // v-model 或 @change
          } else {
            throw new Error(res.data.message || '上传接口返回错误')
          }
        } catch (error) {
          console.error('上传失败:', error)
          this.$q.notify({ message: '上传失败: ' + (error.message || '网络错误'), color: 'negative', position: 'top' })
        } finally {
          this.uploading = false
        }
      })
    }
  }
}
